import React, { useState, useEffect } from "react";
import { Package, Calendar, DollarSign, FileText, ArrowRight, Loader2, Search } from "lucide-react";
import api from "../../../utils/api";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";

const MyPackages = () => {
    const { lang } = useParams();
    const currentLang = lang || 'en';
    const [packages, setPackages] = useState([]);
    
    // ...


    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchMyPackages = async () => {
            try {
                const response = await api.get("/my-packages");
                setPackages(response.data.data);
            } catch (err) {
                console.error("Error fetching my packages:", err);
                setError("Failed to load your packages. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchMyPackages();
    }, []);

    const filteredPackages = packages.filter(pkg =>
        pkg.package.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                <p className="text-gray-500 font-medium">Loading your packages...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 text-red-700 p-6 rounded-2xl border border-red-100 text-center my-10">
                <p className="font-semibold">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="mt-4 text-sm font-bold underline"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">My Purchased Packages</h2>
                    <p className="text-gray-500 mt-1">Manage and access all your active immigration service packages.</p>
                </div>

                <div className="relative group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                        type="text"
                        placeholder="Search packages..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all w-full md:w-64"
                    />
                </div>
            </div>

            {filteredPackages.length === 0 ? (
                <div className="text-center py-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600">No packages found</h3>
                    <p className="text-gray-500 mt-2">You haven't purchased any packages yet or search result is empty.</p>
                    <Link to={`/${currentLang}/forms`} className="mt-6 inline-flex items-center gap-2 text-blue-600 font-bold hover:underline">
                        Browse all forms & packages <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-6">
                    {filteredPackages.map((item, index) => (
                        <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                        >
                            <div className="p-6 md:p-8">
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                                    <div className="flex items-center gap-4">
                                        <div className="bg-blue-100 p-4 rounded-2xl">
                                            <Package className="w-8 h-8 text-blue-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900 leading-tight">
                                                {item.package.name}
                                            </h3>
                                            <div className="flex flex-wrap items-center gap-4 mt-2">
                                                <span className="flex items-center gap-1.5 text-sm text-gray-500 font-medium">
                                                    <Calendar className="w-4 h-4" />
                                                    Purchased: {new Date(item.purchased_at).toLocaleDateString()}
                                                </span>
                                                <span className="flex items-center gap-1.5 text-sm text-gray-500 font-medium">
                                                    <DollarSign className="w-4 h-4" />
                                                    Paid: ${item.amount_paid}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                                        Active
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest pl-1">Included Forms</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {item.package.forms.map((form) => (
                                            <Link
                                                key={form.id}
                                                to={`/${currentLang}/forms/${form.code}`}
                                                className="group flex items-center justify-between p-4 bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-200 rounded-2xl transition-all"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-white p-2 rounded-lg shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                                        <FileText className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors uppercase tracking-tighter">
                                                            {form.code}
                                                        </p>
                                                        <p className="text-xs text-gray-500 group-hover:text-blue-600 transition-colors truncate max-w-[150px]">
                                                            {form.name}
                                                        </p>
                                                    </div>
                                                </div>
                                                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyPackages;
